import { useState, useCallback, useEffect } from 'react';
import { getDeviceInfo } from "../utils/getDeviceInfo";
import { handleError, handleWarning } from '../utils/errorHandling';
import { handleChunkDataAvailable } from '../utils/session/handleChunkDataAvailable';
import startSession from '../utils/session/startSession';
import { useTranslation } from 'react-i18next';
import useWakeLock from './useWakeLock';
import useDetectSilence from './useDetectSilence';
import supportedBrowser from '../utils/supportedBrowser';
import { Bugsnag } from "../bugsnag";
import { logEvent } from '../analytics';

// _initialChunksCount should be used only in tests, to simulate the number of chunks that have been recorded
export const useRecorder = (_initialChunksCount = 0) => {
  // Each audio partial is 30 seconds long
  const PARTIAL_SIZE_IN_SEC = 30;
  const PARTIAL_SIZE_IN_MSEC = PARTIAL_SIZE_IN_SEC * 1000;
  const { t } = useTranslation();

  const [isRecording, setIsRecording] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasPaused, setHasPaused] = useState(false);
  const [sessionUuid, setSessionUuid] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [totalChunks, setTotalChunks] = useState(_initialChunksCount);

  // Silence detection
  const [analyser, setAnalyser] = useState(null);
  const { problemType, problemStats } = useDetectSilence(analyser, isRecording, hasPaused);

  const deviceInfo = getDeviceInfo();
  const isSupportedBrowser = supportedBrowser();

  const { startWakeLock, stopWakeLock } = useWakeLock();

  const isSupportedMediaRecorder = () => {
    return navigator.mediaDevices && navigator.mediaDevices.getUserMedia && typeof MediaRecorder !== 'undefined';
  };

  const getAudioMimeType = () => {
    const supportedAudioFormats = require('../utils/supportedAudioFormats').default;
    return supportedAudioFormats.find((type) => MediaRecorder.isTypeSupported(type));
  };

  // Helper function to reset recorder states
  const resetRecordingState = () => {
    setRecorder(null);
    setIsRecording(false);
    setHasStarted(false);
  };

  const requestAudioPermission = async (uuid) => {
    try {
      const audioMimeType = getAudioMimeType();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options = { mimeType: audioMimeType };
      console.log(`MediaRecorder: using ${audioMimeType} format for audio.`);
      const mediaRecorder = new MediaRecorder(stream, options);
      let currentChunkIndex = 0;

      // context, source and analyser needed for silence detection
      const audioContext = new (window.AudioContext)();
      const audioSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      audioSource.connect(analyser);
      setAnalyser(analyser);

      mediaRecorder.ondataavailable = async (e) => {
        currentChunkIndex = await handleChunkDataAvailable(e, {
          audioMimeType,
          sessionUuid: uuid,
          currentChunkIndex,
          setTotalChunks,
        });
      };

      mediaRecorder.onstop = async () => {
        console.log('MediaRecorder: stopped');
        await stopWakeLock();
      };

      mediaRecorder.onpause = () => {
        console.log('MediaRecorder: paused');
        // Ensure any remaining data is flushed
        mediaRecorder.requestData();
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        Bugsnag.notify(new Error(`mediaRecorder.onerror: ${JSON.stringify(event.error)}`));
        handleError(t('session_recorder.errors.access_device'));
        resetRecordingState();
        throw new Error(t('session_recorder.errors.access_device'));
      };

      setRecorder(mediaRecorder);
      await startWakeLock();
      console.log(`MediaRecorder: starting recording in chunks of ${PARTIAL_SIZE_IN_SEC} seconds`);
      mediaRecorder.start(PARTIAL_SIZE_IN_MSEC);
      setIsRecording(true);
      setHasStarted(true);
    } catch (err) {
      Bugsnag.leaveBreadcrumb(`requestAudioPermission error: ${JSON.stringify(err)}`)
      handleError(t('session_recorder.errors.access_device'));
      resetRecordingState();
      // Propagate the error for the UI to handle
      throw err;
    }
  };

  const toggleRecording = useCallback(async () => {
    if (!isSupportedBrowser || !isSupportedMediaRecorder()) {
      handleWarning(t('session_recorder.errors.unsupported_browser'));
      Bugsnag.notify(new Error(`Unsupported Browser: ${JSON.stringify(deviceInfo)}`));
      throw new Error('Unsupported browser');
    }

    if (!recorder) {
      const newSessionUuid = await startSession();
      if (!newSessionUuid) {
        handleError(t('session_recorder.errors.start'));
        throw new Error(t('session_recorder.errors.start'));
      }
      setSessionUuid(newSessionUuid);
      await requestAudioPermission(newSessionUuid); // May throw if mic access fails.
      logEvent('SessionRecorder', 'Start');
    } else if (recorder && recorder.state === 'recording') {
      recorder.pause();
      setIsRecording(false);
      setHasPaused(true);
    } else if (recorder && recorder.state === 'paused') {
      recorder.resume();
      setIsRecording(true);
      setHasPaused(false);
    }
  }, [recorder]);

  const endRecording = async () => {
    if (recorder) {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      // Only stop tracks if we're not recording
      if (!isRecording) {
        recorder.stream?.getTracks().forEach((track) => track.stop()); // force cleanup
      }
      setIsRecording(false);
      setHasStarted(false);
      await stopWakeLock();
      // Reset total chunks count on full stop
      setTotalChunks(0);
      // Force reinitialization on next start
      resetRecordingState();
    }
  };

  return {
    isRecording,
    toggleRecording,
    endRecording,
    sessionUuid,
    totalChunks,
    problemType,
    problemStats
  };
};
